import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Injector,
  Input,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
import {
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
  NumberSpinnerComponent,
  SpinType,
  UpdateSourceType,
  ZERO,
} from "shared/base-components/number-spinner.component";
import { CurrencyImpl } from "shared/cubiq-model-multi-asset/model/impl/CurrencyImpl";
import { CcyLookupService } from "shared/cubiq-services/lookup/model/impl/CcyLookupService";
// services
import { CcyPromiseLookupService } from "shared/cubiq-services/lookup/model/impl/CcyPromiseLookupService";
import { BfxInputDirective } from "./bfx-input";
import { BfxFieldNumberDirective } from "./bfx-field-number";

export const AMOUNT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AmountSpinnerComponent),
  multi: true,
};

const logger = LoggerService.for(__dirname);

/**
 * Spinner component for displaying amounts. It will defaul to max precision of 2 but can
 * look up currency settings from an input currency code
 */
@Component({
  selector: "amount-spinner",
  templateUrl: "./html/number-spinner.html",
  providers: [AMOUNT_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AmountSpinnerComponent extends NumberSpinnerComponent {
  protected _currency: string = null;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  /** Flag used in rendering oe-panel-gamma viewer - all I know */
  @Input() dead: boolean;

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
    private currencyPromiseService: CcyPromiseLookupService,
    public el: ElementRef,
  ) {
    super(injector, inputDirective, fieldDirective);

    this.minPrecision = 0;
    this.maxPrecision = 2;
    this.min = ZERO;
    this.spinType = SpinType.RELATIVE;
    this.keyboardMultipliersEnabled = true;
    this.mandatory = true;
    this.keyupAssignZero = true;
    this.max = new BigNumber(1e15);
  }

  /**********************************************************************/
  /* INITIALIZATION */

  public get currency(): string {
    return this._currency;
  }

  @Input() public set currency(currency: string) {
    if (currency != null) {
      let currencyLookup: CcyLookupService = this.currencyPromiseService.getBase();
      let ccy: CurrencyImpl = currencyLookup.getItem(currency.toUpperCase());

      if (ccy != null && currency.length === 3) {
        this.maxPrecision = ccy.minorUnitsQty.toNumber();
        this.config.maxPrecision = ccy.minorUnitsQty.toNumber();
      }

      this._currency = currency;
      if (this.data.value) {
        this.updateValue(this.data.value, UpdateSourceType.CONFIG_CHANGE);
        this.formatInputValue();
      }
    }
  }

  public resetInputControl(): void {
    this.minPrecision = 0;
    this.maxPrecision = 2;
    super.resetInputControl();
  }

  public onBlur(event: FocusEvent): void {
    if (this.data.value == null && this.mandatory) {
      this.updateValue(new BigNumber(0), UpdateSourceType.USER_INPUT);
    }
    super.onBlur(event);
  }

  protected override formatNumber(value: BigNumber, formatAsFocused: boolean): string {
    let decimalPlaces: number = value ? value.decimalPlaces() : 0;
    if (decimalPlaces > 0) {
      this.config.minPrecision = this.config.maxPrecision;
    } else {
      this.config.minPrecision = 0;
    }
    return super.formatNumber(value, formatAsFocused);
  }

  protected override updateInputClasses(): void {
    super.updateInputClasses();

    if (this.dead) {
      this.fieldState = "disabled";
    }
  }
}
